import './HeaderWithButtonMenu.scss';

import { isArray } from 'lodash';
import ResizeObserver from 'rc-resize-observer';

import { Col, Dropdown, Row, Space } from 'antd';
import React, { PropsWithChildren, useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react';

const gapSize = 8;

export interface HeaderWithButtonMenuProps {
	title: React.ReactNode;
}

export const HeaderWithButtonMenu: React.FC<PropsWithChildren<HeaderWithButtonMenuProps>> =
	({ children, title }) => {
		const [ overflowCount, setOverflowCount ] = useState(0);
		const buttonWidths = useRef<Array<number> | null>(null);
		const lastRowWidth = useRef<number>();

		const rowRef = useRef<HTMLDivElement>(null);
		const titleRef = useRef<HTMLDivElement>(null);
		const buttonsRef = useRef<HTMLDivElement>(null);

		const buttons = useMemo(() => {
			if (!children) {
				return [];
			}

			if (!isArray(children)) {
				return [ children ];
			}

			return (children as Array<React.ReactNode>).filter((entry) => Boolean(entry));
		}, [ children ]);

		const recalcOverflow = useCallback(() => {
			if (!buttonWidths.current || buttonWidths.current.length <= 1) {
				setOverflowCount(0);

				return;
			}

			const rowWidth = rowRef.current?.getBoundingClientRect().width ?? 0;
			const titleWidth = titleRef.current?.getBoundingClientRect().width ?? 0;
			const buttonsWidth = buttonsRef.current?.getBoundingClientRect().width ?? 0;

			if (lastRowWidth.current === rowWidth) {
				return;
			}

			lastRowWidth.current = rowWidth;

			let overflow = titleWidth + buttonsWidth - rowWidth + gapSize;

			if (overflow > 0) {
				let buttonIndex = 0;

				while (overflow > 0) {
					overflow -= buttonsRef.current?.children[ buttonIndex ].getBoundingClientRect().width ?? 0;
					buttonIndex++;
				}

				setOverflowCount(overflowCount + buttonIndex);

				return;
			}

			if (overflowCount && overflow < 0) {
				let buttonIndex = overflowCount - 1;

				while (overflow < 0 && buttonIndex >= 0) {
					overflow += buttonWidths.current[ buttonIndex ] + gapSize;

					if (overflow > 0) {
						break;
					}

					buttonIndex--;
				}

				setOverflowCount(buttonIndex + 1);
			}
		}, [ overflowCount ]);

		useLayoutEffect(() => {
			if (!buttonWidths.current) {
				const buttonElements = [ ...buttonsRef.current?.children ?? [] ];
				buttonWidths.current = buttonElements.map((element) => element.getBoundingClientRect().width);
			}

			recalcOverflow();
		}, [ recalcOverflow ]);

		const managedButtons = useMemo(() => {
			if (!buttonWidths.current || buttons.length <= 1 || !overflowCount) {
				return buttons;
			}

			const rootButton = buttons[ buttons.length - 1 ];
			const menuButtons = buttons.slice(0, overflowCount).reverse();
			const remainingButtons = buttons.slice(overflowCount, -1);

			return (
				<Space
					className='button-menu-space collapsed'
					size='small'>
					{ remainingButtons }
					<Dropdown.Button
						buttonsRender={ ([ , dropdownButton ]) => [
							rootButton,
							dropdownButton
						] }
						popupRender={ () =>
							<Space
								direction='vertical'
								size='small'>{ menuButtons }
							</Space> } />
				</Space>
			);
		}, [ buttons, overflowCount ]);

		return (
			<ResizeObserver
				onResize={ recalcOverflow }>
				<Row
					align='middle'
					className='button-menu-header'
					justify='space-between'
					ref={ rowRef }>
					<Col ref={ titleRef }>{ title }</Col>
					<Col>
						<Space
							className='button-menu-space'
							ref={ buttonsRef }
							size='small'>
							{ managedButtons }
						</Space>
					</Col>
				</Row>
			</ResizeObserver>
		);
	};
